import React, { useCallback, useEffect, useState } from 'react';
import { GridColDef, GridInputRowSelectionModel, GridSortModel } from '@mui/x-data-grid-pro';
import { SubMenu } from 'components/table/combination_rule/SubMenu';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Grid } from '@mui/material';
import { TimekeeperService } from 'services/TimekeeperService';

import('dayjs/locale/es');
dayjs.locale('es');

interface OvertimeRule {
    name: string;
}

interface Zone {
    name: string;
}

interface ScheduleDesviationRule {
    name: string;
}

interface CombinationRow {
    id: number;
    name: string;
    description: string;
    overtime_rules: OvertimeRule[];
    zones: Zone[];
    schedule_desviation_rules: ScheduleDesviationRule[];
}

interface ContextMenuState {
    mouseX: number;
    mouseY: number;
}

const tk = new TimekeeperService();

export const CombinationTable: React.FC = () => {
    const { t } = useTranslation();

    const columns: GridColDef[] = [
        {
            field: 'name',
            headerName: t('settings.combinations.table.columns.name'),
            flex: 1,
        },
        {
            field: 'overtime_rules',
            headerName: t('settings.combinations.table.columns.overtimes_rules'),
            flex: 1,
            renderCell: (params) => {
                return params.value.map((overtime: OvertimeRule) => overtime.name).join(', ');
            },
        },
        {
            field: 'zones',
            headerName: t('settings.combinations.table.columns.zones_rules'),
            flex: 1,
            renderCell: (params) => {
                return params.value.map((zone: Zone) => zone.name).join(', ');
            },
        },
        {
            field: 'schedule_desviation_rules',
            headerName: t('settings.combinations.table.columns.schedule_desviation_rules'),
            flex: 1,
            renderCell: (params) => {
                return params.value
                    .map((schedule_desviation: ScheduleDesviationRule) => schedule_desviation.name)
                    .join(', ');
            },
        },
    ];

    const [rows, setRows] = useState<CombinationRow[]>([]);
    const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [selected, setSelected] = useState<GridInputRowSelectionModel>([]);
    const [selectedData, setSelectedData] = useState<CombinationRow[]>([]);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'name',
            sort: 'asc',
        },
    ]);

    const reloadingItems = useCallback(() => {
        setLoading(true);
        tk.combination_rule
            .getCombinations()
            .then((response) => {
                const rows = response.map((combination) => ({
                    id: combination.combination_rule_id,
                    name: combination.name,
                    description: combination.description,
                    overtime_rules: combination.overtime_rules,
                    zones: combination.zones,
                    schedule_desviation_rules: combination.schedule_desviation_rules,
                }));
                const orderdRows = rows.sort((a, b) => {
                    const nameA = a.name.toUpperCase();
                    const nameB = b.name.toUpperCase();
                    if (nameA < nameB) {
                        return -1;
                    }
                    if (nameA > nameB) {
                        return 1;
                    }
                    return 0;
                });
                setRows(orderdRows);
            })
            .catch((error) => {
                console.error(error);
                setRows([]);
            })
            .finally(() => {
                setLoading(false);
                setSelected([]);
                setSelectedData([]);
            });
    }, []);

    useEffect(() => {
        reloadingItems();
    }, [reloadingItems]);

    return (
        <Grid
            container
            style={{ width: '100%', height: '100%' }}
            onContextMenu={(event) => {
                event.preventDefault();
                setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null);
            }}
        >
            <DataGridPro
                columns={columns}
                rows={rows}
                sx={{ height: 'calc(100vh - 140px)' }}
                showCellVerticalBorder
                showColumnVerticalBorder
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                disableColumnMenu
                loading={loading}
                checkboxSelection
                pagination
                sortModel={sortModel}
                onSortModelChange={(model) => setSortModel(model)}
                onRowSelectionModelChange={(newRowSelectionModel) => {
                    let data = rows.filter((row) => newRowSelectionModel.includes(row.id));
                    setSelected(newRowSelectionModel);
                    setSelectedData(data);
                }}
                rowSelectionModel={selected}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                    },
                }}
                localeText={{
                    columnMenuLabel: t('general.table.headerFilters.columnMenuLabel'),
                    columnMenuSortAsc: t('general.table.headerFilters.columnMenuSortAsc'),
                    columnMenuSortDesc: t('general.table.headerFilters.columnMenuSortDesc'),
                    pinToLeft: t('general.table.cellFilters.pinToLeft'),
                    pinToRight: t('general.table.cellFilters.pinToRight'),
                    toolbarQuickFilterPlaceholder: t('general.table.headerFilters.toolbarQuickFilterPlaceholder'),
                    toolbarQuickFilterDeleteIconLabel: t(
                        'general.table.headerFilters.toolbarQuickFilterDeleteIconLabel',
                    ),
                    footerRowSelected: (count) =>
                        count > 1
                            ? t('general.table.footerFilters.footerRowSelected.>1', { count })
                            : t('general.table.footerFilters.footerRowSelected.=1', { count }),

                    footerTotalRows: t('general.table.footerFilters.footerTotalRows'),

                    MuiTablePagination: {
                        labelDisplayedRows: ({ from, to, count }) =>
                            t('general.table.footerFilters.labelDisplayedRows', {
                                from,
                                to,
                                count,
                            }),
                        labelRowsPerPage: t('general.table.footerFilters.labelRowsPerPage'),
                    },
                }}
            />
            <SubMenu
                contextMenu={contextMenu}
                setContextMenu={setContextMenu}
                selectedRow={selectedData}
                reloading={reloadingItems}
                rows={rows}
            />
        </Grid>
    );
};
